/**
 * Coverage — the Monday-morning view.
 *
 * On Mondays April assigns the week's facility visits to providers by
 * distance from each provider's home. Until now the distance was judged by
 * eye and the answer lived only on the Monday board. This screen puts the
 * board, the distances and the roster side by side, and shows only what is
 * worth a person's attention: nobody assigned, a provider who has left, a
 * provider going a long way past someone nearer, or a record awaiting
 * confirmation.
 *
 * It does not decide. The nearest provider is one input; credentialing at
 * the building, the relationship, who is out that week are others and are
 * not in a database. A person assigns; the platform records who.
 *
 * Facilities and providers only. There is no patient anywhere on this page
 * and no path from it to a chart — coverage is territory. Chart access is
 * granted separately, per visit, on the day.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Compass, Loader2, AlertTriangle, Check, ChevronDown, ChevronRight, MapPin, UserX, Route,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../services/api';

type Flag =
  | 'UNASSIGNED' | 'FORMER_PROVIDER' | 'NO_FACILITY_LOCATION' | 'NO_PROVIDER_HOME'
  | 'CLOSER_PROVIDER_EXISTS' | 'AWAITING_CONFIRMATION' | 'RECORDS_DISAGREE';

type Precision = 'EXACT' | 'APPROXIMATE' | null;

interface EmergeAssignment {
  id: string;
  provider_employee_id: string;
  provider_name: string;
  status: 'PROPOSED' | 'ACTIVE';
  coverage_type: 'PRIMARY' | 'COVERING' | 'SUPPORTING';
  source_system: string;
  assignment_method: string;
}

interface BoardRow {
  scheduling_id: string;
  board_status: string | null;
  next_visit_date: string | null;
  last_visit_date: string | null;
  facility_id: string;
  facility_name: string;
  city: string | null;
  state: string | null;
  facility_geocoded: boolean;
  assigned_provider_monday_user_id: string | null;
  assigned_employee_id: string | null;
  assigned_provider_name: string | null;
  assigned_home_precision: Precision;
  monday_assignee_name: string | null;
  monday_assignee_enabled: boolean | null;
  assigned_miles: number | null;
  nearest_employee_id: string | null;
  nearest_provider_name: string | null;
  nearest_home_precision: Precision;
  nearest_miles: number | null;
  emerge_assignments: EmergeAssignment[];
  flags: Flag[];
}

interface Board {
  items: BoardRow[];
  summary: {
    total: number; unassigned: number; formerProvider: number; closerProviderExists: number;
    awaitingConfirmation: number; recordsDisagree: number; needsDecision: number;
  };
  thresholdMiles: number;
  distance: string;
}

interface RankedProvider {
  employee_id: string;
  first_name: string;
  last_name: string;
  email: string;
  home_precision: Precision;
  miles: number | null;
  monday_facility_count: number;
  emerge_active_count: number;
  assignment_here: { id: string; status: 'PROPOSED' | 'ACTIVE'; coverage_type: string; source_system: string } | null;
}

interface Nearest {
  facility: { id: string; name: string; city: string | null; state: string | null; geocoded: boolean };
  providers: RankedProvider[];
}

type Body<T> = { success?: boolean; error?: string; message?: string; data?: T };
type View = 'decision' | 'upcoming' | 'all';

/**
 * What "needs a decision" means. Awaiting confirmation is not in it: a
 * Monday-derived proposal mirrors the board, and on the first real run 195 of
 * 308 rows carried it — a decision view that is the whole board shows
 * nothing. The chip and its tile stay; the default view is the rows where
 * nobody is going, or someone who has left is.
 */
const DECISION_FLAGS: Flag[] = [
  'UNASSIGNED', 'FORMER_PROVIDER', 'CLOSER_PROVIDER_EXISTS', 'RECORDS_DISAGREE',
];

const FLAG_LABEL: Record<Flag, string> = {
  UNASSIGNED: 'No provider',
  FORMER_PROVIDER: 'Former provider',
  CLOSER_PROVIDER_EXISTS: 'Closer provider',
  AWAITING_CONFIRMATION: 'Awaiting confirmation',
  RECORDS_DISAGREE: 'Records disagree',
  NO_PROVIDER_HOME: 'No home location',
  NO_FACILITY_LOCATION: 'Facility not mapped',
};

const FLAG_TONE: Record<Flag, string> = {
  UNASSIGNED: 'text-rose-700 bg-rose-50 border-rose-200',
  FORMER_PROVIDER: 'text-rose-700 bg-rose-50 border-rose-200',
  CLOSER_PROVIDER_EXISTS: 'text-amber-800 bg-amber-50 border-amber-200',
  AWAITING_CONFIRMATION: 'text-emerald-800 bg-emerald-50 border-emerald-200',
  RECORDS_DISAGREE: 'text-amber-800 bg-amber-50 border-amber-200',
  NO_PROVIDER_HOME: 'text-gray-600 bg-gray-50 border-gray-200',
  NO_FACILITY_LOCATION: 'text-gray-600 bg-gray-50 border-gray-200',
};

const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/** "12.4 mi", with the precision made visible when it is not a front door. */
const fmtMiles = (miles: number | null, precision: Precision) => {
  if (miles === null || miles === undefined) return null;
  return `${miles.toFixed(1)} mi${precision === 'APPROXIMATE' ? ' (approx.)' : ''}`;
};

const isoToday = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};
const isoPlusDays = (n: number) => {
  const t = new Date();
  t.setDate(t.getDate() + n);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

export default function CoverageBoard() {
  const [board, setBoard] = useState<Board | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('decision');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [nearest, setNearest] = useState<Record<string, Nearest | 'loading'>>({});
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // `api.get` returns the response body, and resolves `{success:false}`
      // rather than throwing — see AccessReview for the defect that taught us.
      const res = (await api.get('/emerge/geo/coverage')) as Body<Board>;
      if (res?.success === false) throw new Error(res.error || res.message || 'Request failed');
      setBoard(res?.data ?? null);
    } catch (err: any) {
      toast.error(err?.message ? `Could not load coverage — ${err.message}` : 'Could not load coverage.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadNearest = useCallback(async (facilityId: string) => {
    setNearest((n) => ({ ...n, [facilityId]: 'loading' }));
    try {
      const res = (await api.get(`/emerge/geo/facilities/${facilityId}/nearest-providers`)) as Body<Nearest>;
      if (res?.success === false || !res?.data) throw new Error(res?.error || 'Request failed');
      setNearest((n) => ({ ...n, [facilityId]: res.data as Nearest }));
    } catch (err: any) {
      toast.error(err?.message || 'Could not rank providers for this facility.');
      setNearest((n) => { const { [facilityId]: _omit, ...rest } = n; return rest; });
    }
  }, []);

  const toggle = (row: BoardRow) => {
    const open = expanded === row.facility_id;
    setExpanded(open ? null : row.facility_id);
    if (!open && !nearest[row.facility_id]) loadNearest(row.facility_id);
  };

  /** Record an assignment in eMerge. Lands ACTIVE, authored by the caller. */
  const assign = async (row: BoardRow, p: RankedProvider, coverageType: 'PRIMARY' | 'SUPPORTING') => {
    setBusy(p.employee_id);
    try {
      const distance = fmtMiles(p.miles, p.home_precision);
      const res = (await api.post('/emerge/provider-assignments/facilities', {
        providerEmployeeId: p.employee_id,
        facilityId: row.facility_id,
        coverageType,
        notes: `Coverage board${distance ? ` — ${distance} straight-line from home` : ''}`,
      })) as Body<unknown>;
      if (res?.success === false) {
        toast.error(res.error || res.message || 'Could not record the assignment.');
        return;
      }
      toast.success(`${p.first_name} ${p.last_name} assigned to ${row.facility_name}.`);
      await Promise.all([load(), loadNearest(row.facility_id)]);
    } catch (err: any) {
      toast.error(err?.message || 'Could not record the assignment.');
    } finally {
      setBusy(null);
    }
  };

  /** Confirm or reject a Monday-derived proposal. Rejection needs a reason. */
  const decide = async (row: BoardRow, assignmentId: string, decision: 'CONFIRM' | 'REJECT') => {
    const reason = (rejectReason[assignmentId] || '').trim();
    if (decision === 'REJECT' && reason.length < 3) {
      toast.error('Say why this proposal is wrong — it is stored with your name.');
      return;
    }
    setBusy(assignmentId);
    try {
      const res = (await api.post(`/emerge/provider-assignments/facilities/${assignmentId}/decision`, {
        decision, reason: reason || undefined,
      })) as Body<unknown>;
      if (res?.success === false) {
        toast.error(res.error || res.message || 'Could not record the decision.');
        return;
      }
      toast.success(decision === 'CONFIRM' ? 'Confirmed.' : 'Rejected.');
      await Promise.all([load(), loadNearest(row.facility_id)]);
    } catch (err: any) {
      toast.error(err?.message || 'Could not record the decision.');
    } finally {
      setBusy(null);
    }
  };

  const providers = useMemo(() => {
    const seen = new Map<string, string>();
    for (const r of board?.items ?? []) {
      if (r.assigned_employee_id && r.assigned_provider_name) seen.set(r.assigned_employee_id, r.assigned_provider_name);
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [board]);

  const rows = useMemo(() => {
    const items = board?.items ?? [];
    const today = isoToday();
    const horizon = isoPlusDays(14);
    return items.filter((r) => {
      if (providerFilter && r.assigned_employee_id !== providerFilter) return false;
      if (view === 'decision') return r.flags.some((f) => DECISION_FLAGS.includes(f));
      if (view === 'upcoming') return Boolean(r.next_visit_date && r.next_visit_date >= today && r.next_visit_date <= horizon);
      return true;
    });
  }, [board, view, providerFilter]);

  const s = board?.summary;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Compass className="w-6 h-6 text-emerald-600" />
            Coverage
          </h1>
          <p className="text-sm text-gray-600 mt-1 max-w-3xl">
            Every facility on the scheduling board: who Monday says is going, how far that is
            from their home, and who lives nearest. Distances are straight-line. Monday is still
            where the week is scheduled — recording an assignment here makes eMerge's record
            match it, under your name.
          </p>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            ['Need a decision', s.needsDecision, s.needsDecision ? 'text-rose-700' : 'text-gray-900'],
            ['No provider', s.unassigned, 'text-gray-900'],
            ['Former provider', s.formerProvider, 'text-gray-900'],
            [`Closer by ${board?.thresholdMiles}+ mi`, s.closerProviderExists, 'text-gray-900'],
            ['Awaiting confirmation', s.awaitingConfirmation, 'text-gray-900'],
          ].map(([label, n, tone]) => (
            <div key={String(label)} className="border border-gray-200 rounded-lg p-4 bg-white">
              <div className={`text-2xl font-semibold tabular-nums ${tone}`}>{n}</div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {([
          ['decision', 'Needs a decision'],
          ['upcoming', 'Next 14 days'],
          ['all', 'Whole board'],
        ] as Array<[View, string]>).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setView(key)}
            className={`text-sm px-3 py-1 rounded border ${view === key
              ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
          >
            {label}
          </button>
        ))}
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          aria-label="Filter by assigned provider"
          className="ml-auto text-sm border border-gray-300 rounded px-2 py-1 bg-white"
        >
          <option value="">Every provider</option>
          {providers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-gray-500 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      )}

      {!loading && board && rows.length === 0 && (
        <div className="text-center py-16 text-gray-500 border border-dashed border-gray-300 rounded-lg">
          {view === 'decision' ? 'Nothing on the board needs a decision.' : 'No facilities match.'}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Facility</th>
                <th className="text-left px-3 py-2 font-medium">Board</th>
                <th className="text-left px-3 py-2 font-medium">Next visit</th>
                <th className="text-left px-3 py-2 font-medium">Monday says</th>
                <th className="text-left px-3 py-2 font-medium">Nearest</th>
                <th className="text-left px-3 py-2 font-medium">eMerge record</th>
                <th className="text-left px-3 py-2 font-medium">Attention</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row) => {
                const open = expanded === row.facility_id;
                const ranking = nearest[row.facility_id];
                const activePrimary = row.emerge_assignments.find((a) => a.status === 'ACTIVE' && a.coverage_type === 'PRIMARY');
                return (
                  <React.Fragment key={row.scheduling_id}>
                    <tr className={open ? 'bg-emerald-50/40' : 'hover:bg-gray-50'}>
                      <td className="px-4 py-2.5">
                        <div className="text-gray-900">{row.facility_name}</div>
                        <div className="text-xs text-gray-500">
                          {[row.city, row.state].filter(Boolean).join(', ')}
                          {!row.facility_geocoded && (
                            <span className="ml-2 inline-flex items-center gap-1 text-gray-500"><MapPin className="w-3 h-3" /> not mapped</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600 whitespace-nowrap">{row.board_status || '—'}</td>
                      <td className="px-3 py-2.5 text-gray-700 tabular-nums whitespace-nowrap">{fmtDate(row.next_visit_date)}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {row.assigned_employee_id ? (
                          <>
                            <div className="text-gray-900">{row.assigned_provider_name}</div>
                            <div className="text-xs text-gray-500 tabular-nums">
                              {fmtMiles(row.assigned_miles, row.assigned_home_precision) || 'distance unknown'}
                            </div>
                          </>
                        ) : row.assigned_provider_monday_user_id ? (
                          <div className="inline-flex items-center gap-1 text-rose-700">
                            <UserX className="w-3.5 h-3.5" />
                            <span>{row.monday_assignee_name || 'unknown Monday user'}</span>
                          </div>
                        ) : (
                          <span className="text-rose-700">— nobody</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap">
                        {row.nearest_employee_id ? (
                          <>
                            <div className={row.nearest_employee_id === row.assigned_employee_id ? 'text-gray-500' : 'text-gray-900'}>
                              {row.nearest_provider_name}
                            </div>
                            <div className="text-xs text-gray-500 tabular-nums">
                              {fmtMiles(row.nearest_miles, row.nearest_home_precision)}
                            </div>
                          </>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-3 py-2.5 text-xs whitespace-nowrap">
                        {row.emerge_assignments.length === 0 && <span className="text-gray-400">none</span>}
                        {row.emerge_assignments.map((a) => (
                          <div key={a.id} className={a.status === 'ACTIVE' ? 'text-gray-900' : 'text-emerald-800'}>
                            {a.provider_name}
                            <span className="text-gray-500"> · {a.status === 'ACTIVE' ? a.coverage_type.toLowerCase() : 'proposed'}</span>
                          </div>
                        ))}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex flex-wrap gap-1">
                          {row.flags.map((f) => (
                            <span key={f} className={`inline-flex items-center text-xs border rounded px-1.5 py-0.5 whitespace-nowrap ${FLAG_TONE[f]}`}>
                              {FLAG_LABEL[f]}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-right">
                        <button
                          type="button"
                          onClick={() => toggle(row)}
                          aria-expanded={open}
                          aria-label={`${open ? 'Hide' : 'Show'} providers for ${row.facility_name}`}
                          className="p-1 rounded text-gray-500 hover:bg-gray-100"
                        >
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>

                    {open && (
                      <tr className="bg-gray-50">
                        <td colSpan={8} className="px-4 pb-4 pt-2">
                          {ranking === 'loading' || !ranking ? (
                            <div className="flex items-center gap-2 text-gray-500 py-4 text-sm">
                              <Loader2 className="w-4 h-4 animate-spin" /> Ranking providers by distance…
                            </div>
                          ) : (
                            <div>
                              <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                                <Route className="w-3.5 h-3.5" />
                                Straight-line from each provider's home to {ranking.facility.name}.
                                {!ranking.facility.geocoded && ' This facility has no map location, so no distances can be shown.'}
                              </div>
                              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white divide-y divide-gray-100">
                                {ranking.providers.map((p) => {
                                  const here = p.assignment_here;
                                  const isMonday = p.employee_id === row.assigned_employee_id;
                                  const distance = fmtMiles(p.miles, p.home_precision);
                                  return (
                                    <div key={p.employee_id} className="flex items-center gap-4 px-4 py-2.5 text-sm flex-wrap">
                                      <div className="min-w-0 flex-1">
                                        <span className="text-gray-900">{p.first_name} {p.last_name}</span>
                                        {isMonday && <span className="ml-2 text-xs text-gray-500">on the Monday board</span>}
                                      </div>
                                      <div className="w-32 tabular-nums text-gray-700">
                                        {distance || <span className="text-gray-400">no home location</span>}
                                      </div>
                                      <div className="w-32 text-xs text-gray-500 tabular-nums">
                                        {p.monday_facility_count} on board
                                      </div>
                                      <div className="shrink-0 flex items-center gap-2">
                                        {here?.status === 'ACTIVE' && (
                                          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                                            <Check className="w-3.5 h-3.5" /> active · {here.coverage_type.toLowerCase()}
                                          </span>
                                        )}
                                        {here?.status === 'PROPOSED' && (
                                          <>
                                            <input
                                              value={rejectReason[here.id] || ''}
                                              onChange={(e) => setRejectReason((r) => ({ ...r, [here.id]: e.target.value }))}
                                              placeholder="reason, if rejecting"
                                              aria-label={`Reason for rejecting ${p.first_name} ${p.last_name}`}
                                              className="text-xs border border-gray-300 rounded px-2 py-1 w-44"
                                            />
                                            <button
                                              type="button"
                                              disabled={busy === here.id}
                                              onClick={() => decide(row, here.id, 'REJECT')}
                                              className="text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                                            >
                                              Reject
                                            </button>
                                            <button
                                              type="button"
                                              disabled={busy === here.id}
                                              onClick={() => decide(row, here.id, 'CONFIRM')}
                                              className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                                            >
                                              {busy === here.id ? 'Saving…' : 'Confirm'}
                                            </button>
                                          </>
                                        )}
                                        {!here && (
                                          <button
                                            type="button"
                                            disabled={busy === p.employee_id}
                                            onClick={() => assign(row, p, activePrimary ? 'SUPPORTING' : 'PRIMARY')}
                                            className="text-xs px-3 py-1 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                                          >
                                            {busy === p.employee_id ? 'Saving…' : activePrimary ? 'Assign as supporting' : 'Assign'}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              {row.flags.includes('FORMER_PROVIDER') && (
                                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-3 flex items-start gap-2">
                                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                  <span>
                                    Monday still names <strong>{row.monday_assignee_name || 'a user'}</strong>, who is
                                    not on the active roster. Assigning here records eMerge's answer; the Monday
                                    board still needs updating so the schedule agrees.
                                  </span>
                                </p>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-500 mt-8 border-t border-gray-200 pt-4 max-w-3xl">
        Nearest is not the same as right: credentialing at the building, the relationship,
        and who is out that week are not in this table. A facility assignment is territory —
        it opens no chart. Chart access is granted per visit, on the day, separately.
      </p>
    </div>
  );
}
